import {
  ArrowDown,
  ArrowUp,
  CalendarPlus,
  CaretDown,
  Check,
  CheckCircle,
  DotsSixVertical,
  Link as LinkIcon,
  Paperclip,
  Plus,
  Star,
  Trash,
  UploadSimple,
  Warning,
  X,
} from '@phosphor-icons/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDeferredValue, useEffect, useMemo, useRef, useState, type DragEvent, type FormEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useOutletContext, useSearchParams } from 'react-router-dom'
import type { DashboardOutletContext } from '../components/AppShell'
import { Button, ConflictDialog, Drawer, Field, Panel } from '../components/common'
import { HistoryPanel } from '../components/HistoryPanel'
import { api, conflictCurrent } from '../lib/api'
import { workingItemOwners, workingStatusLabel, workingStatusLight, workingStatusOptions } from '../lib/constants'
import { resolveDeepLinkDrawer } from '../lib/deep-link-drawer'
import { useDraft } from '../lib/draft'
import { compressImageToWebp, formatBytes, IMAGE_LIMITS, type CompressedImage } from '../lib/image-compression'
import { sortOrderBefore } from '../lib/sort'
import { enforceWorkingItemCompletion, nextWorkingItemSort, sortWorkingItems, workingProjectNames, workingProjectRefs, workingProjectSummary, type WorkingItemSort, type WorkingItemSortKey } from '../lib/working-item-rules'
import type { DashboardData, Project, ReadinessItem, Subtask, WorkingAttachment, WorkingItem, WorkingLink } from '../lib/types'

interface PendingAttachment extends CompressedImage {
  id: string
  previewUrl: string
}

export type WorkingForm = Pick<WorkingItem, 'projectIds' | 'task' | 'owner' | 'startDate' | 'dueDate' | 'status' | 'done' | 'isPriority' | 'subtasks' | 'links' | 'notes' | 'readinessItemIds'>

export const emptyWorkingForm: WorkingForm = {
  projectIds: [],
  task: '',
  owner: '',
  startDate: '',
  dueDate: '',
  status: 'green',
  done: false,
  isPriority: false,
  subtasks: [],
  links: [],
  notes: '',
  readinessItemIds: [],
}

const emptyForm = emptyWorkingForm

const labels = {
  projectIds: 'Project',
  task: '工作項目',
  owner: 'Owner',
  startDate: '開始日期',
  dueDate: '到期日期',
  status: '狀態',
  done: '完成',
  isPriority: '重點項目',
  subtasks: '工作細項',
  links: '參考連結',
  readinessItemIds: '關聯 Test Item',
  notes: '備註',
}

const workingSortLabels: Record<WorkingItemSortKey, string> = {
  project: 'Project',
  startDate: 'Start',
  dueDate: 'Due',
  status: 'Status',
}

function taipeiDateStamp(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]))
  return `[${values.year}${values.month}${values.day}] `
}

export function WorkingSortHeader({ label, sortKey, sort, onSort, className = '' }: {
  label: string
  sortKey: WorkingItemSortKey
  sort: WorkingItemSort | null
  onSort: () => void
  className?: string
}) {
  const direction = sort?.key === sortKey ? sort.direction : null
  return (
    <th className={className} aria-sort={direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none'}>
      <button
        type="button"
        className={`working-sort-header ${direction ? 'active' : ''}`}
        aria-label={`${label} 排序`}
        title={!direction ? '點擊升冪排序' : direction === 'asc' ? '目前升冪；點擊改為降冪' : '目前降冪；點擊恢復預設'}
        onClick={onSort}
      >
        <span>{label}</span>
        {direction === 'asc' ? <ArrowUp size={12} weight="bold" /> : direction === 'desc' ? <ArrowDown size={12} weight="bold" /> : <span className="working-sort-neutral" aria-hidden="true">↕</span>}
      </button>
    </th>
  )
}

export function WorkingItemsPage() {
  const { data, canEdit, isAdmin } = useOutletContext<DashboardOutletContext>()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [query, setQuery] = useState('')
  const deferredQuery = useDeferredValue(query)
  const [status, setStatus] = useState('全部')
  const [selectedOwner, setSelectedOwner] = useState<string | null>(null)
  const [selectedProject, setSelectedProject] = useState<string | null>(null)
  const [priorityOnly, setPriorityOnly] = useState(false)
  const [editing, setEditing] = useState<WorkingItem | null>(null)
  const [form, setForm, clearDraft] = useDraft<WorkingForm>('working-item', emptyForm)
  const [open, setOpen] = useState(false)
  const [conflict, setConflict] = useState<WorkingItem | null>(null)
  const [message, setMessage] = useState('')
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [tableSort, setTableSort] = useState<WorkingItemSort | null>(null)
  const [expandedTasks, setExpandedTasks] = useState<Record<string, boolean>>({})
  const [openLinksMenuId, setOpenLinksMenuId] = useState<string | null>(null)
  const [openProjectsMenuId, setOpenProjectsMenuId] = useState<string | null>(null)
  const [pendingIds, setPendingIds] = useState<Record<string, boolean>>({})
  const [completedOpen, setCompletedOpen] = useState(false)
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([])
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<string[]>([])
  const [attachmentBusy, setAttachmentBusy] = useState(false)
  const [attachmentMessage, setAttachmentMessage] = useState('')
  const pendingAttachmentsRef = useRef<PendingAttachment[]>([])
  const lastOpenedDeepLinkRef = useRef<string | null>(null)
  const requestedItemId = searchParams.get('item')

  const attachmentsQuery = useQuery({
    queryKey: ['working-attachments', editing?.id],
    queryFn: () => api.workingAttachments(editing!.id),
    enabled: open && Boolean(editing?.id),
  })

  useEffect(() => {
    if (!message) return
    const timer = setTimeout(() => setMessage(''), 2500)
    return () => clearTimeout(timer)
  }, [message])

  useEffect(() => {
    if (!openLinksMenuId && !openProjectsMenuId) return
    const closeMenu = () => { setOpenLinksMenuId(null); setOpenProjectsMenuId(null) }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu()
    }
    window.addEventListener('click', closeMenu)
    window.addEventListener('keydown', closeOnEscape)
    return () => {
      window.removeEventListener('click', closeMenu)
      window.removeEventListener('keydown', closeOnEscape)
    }
  }, [openLinksMenuId, openProjectsMenuId])

  useEffect(() => {
    pendingAttachmentsRef.current = pendingAttachments
  }, [pendingAttachments])

  useEffect(() => () => {
    pendingAttachmentsRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl))
  }, [])

  const toggleExpand = (id: string) => setExpandedTasks((prev) => ({ ...prev, [id]: !prev[id] }))

  const updateWorkingItemInCache = (updatedItem: WorkingItem) => {
    queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
      if (!old) return old
      return {
        ...old,
        workingItems: old.workingItems.map((item) =>
          item.id === updatedItem.id
            ? { ...updatedItem, attachmentCount: updatedItem.attachmentCount ?? item.attachmentCount }
            : item,
        ),
      }
    })
  }

  const patchWorkingItemInCache = (id: string, patch: Partial<WorkingItem>) => {
    queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
      if (!old) return old
      return {
        ...old,
        workingItems: old.workingItems.map((item) =>
          item.id === id ? { ...item, ...patch } : item,
        ),
      }
    })
  }

  const toggleSubtask = async (row: WorkingItem, subtaskId: string) => {
    if (pendingIds[row.id]) return
    const nextSubtasks = (row.subtasks || []).map((st) =>
      st.id === subtaskId ? { ...st, done: !st.done } : st,
    )
    patchWorkingItemInCache(row.id, { subtasks: nextSubtasks })
    setPendingIds((prev) => ({ ...prev, [row.id]: true }))
    try {
      const updated = await api.updateEntity<WorkingItem>('working-items', row.id, row.version, { subtasks: nextSubtasks })
      updateWorkingItemInCache(updated)
    } catch (error) {
      patchWorkingItemInCache(row.id, { subtasks: row.subtasks })
      const latest = conflictCurrent<WorkingItem>(error)
      latest ? showInlineConflict(row, latest) : setMessage(error instanceof Error ? error.message : '更新細項失敗。')
    } finally {
      setPendingIds((prev) => {
        const next = { ...prev }
        delete next[row.id]
        return next
      })
    }
  }

  const filteredRows = useMemo(
    () => data.workingItems.filter((row) =>
      (!priorityOnly || row.isPriority)
      && (status === '全部' || workingStatusLight(row.status) === status)
      && (!selectedOwner || parseOwners(row.owner).includes(selectedOwner))
      && (!selectedProject || (selectedProject === '不分專案' ? workingProjectRefs(row).length === 0 : workingProjectNames(row).includes(selectedProject)))
      && `${workingProjectNames(row).join(' ')} ${workingProjectRefs(row).map((project) => project.groupName).join(' ')} ${row.task} ${row.owner} ${row.notes}`.toLowerCase().includes(deferredQuery.toLowerCase()),
    ),
    [data.workingItems, deferredQuery, priorityOnly, selectedOwner, selectedProject, status],
  )

  const activeRows = useMemo(() => sortWorkingItems(filteredRows.filter((row) => !row.done), tableSort), [filteredRows, tableSort])
  const completedRows = useMemo(() => sortWorkingItems(filteredRows.filter((row) => row.done), tableSort), [filteredRows, tableSort])
  const changeTableSort = (key: WorkingItemSortKey) => {
    setDraggingId(null)
    setTableSort((current) => nextWorkingItemSort(current, key))
  }

  const save = useMutation({
    mutationFn: async () => {
      const savedItem = editing
        ? await api.updateEntity<WorkingItem>('working-items', editing.id, editing.version, form)
        : await api.createEntity<WorkingItem>('working-items', form)
      const failedPendingIds: string[] = []
      const failedDeleteIds: string[] = []
      let uploadedCount = 0
      let deletedCount = 0

      for (const attachmentId of removedAttachmentIds) {
        try {
          await api.deleteWorkingAttachment(savedItem.id, attachmentId)
          deletedCount += 1
        } catch {
          failedDeleteIds.push(attachmentId)
        }
      }
      for (const pending of pendingAttachments) {
        try {
          await api.uploadWorkingAttachment(savedItem.id, pending)
          uploadedCount += 1
        } catch {
          failedPendingIds.push(pending.id)
        }
      }

      const startingCount = attachmentsQuery.data?.length ?? editing?.attachmentCount ?? 0
      return {
        savedItem: {
          ...savedItem,
          attachmentCount: Math.max(0, startingCount - deletedCount + uploadedCount),
        },
        failedPendingIds,
        failedDeleteIds,
      }
    },
    onSuccess: ({ savedItem, failedPendingIds, failedDeleteIds }) => {
      clearDraft()
      queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
        if (!old) return old
        const linkedReadinessIds = new Set(savedItem.readinessItemIds)
        const readiness = old.readiness.map((readinessItem) => ({
          ...readinessItem,
          workingItemIds: linkedReadinessIds.has(readinessItem.id)
            ? [...new Set([...(readinessItem.workingItemIds ?? []), savedItem.id])]
            : (readinessItem.workingItemIds ?? []).filter((workingItemId) => workingItemId !== savedItem.id),
        }))
        if (editing) {
          return {
            ...old,
            readiness,
            workingItems: old.workingItems.map((item) =>
              item.id === savedItem.id ? savedItem : item,
            ),
          }
        }
        return {
          ...old,
          readiness,
          workingItems: [...old.workingItems, savedItem],
        }
      })
      void queryClient.invalidateQueries({ queryKey: ['working-attachments', savedItem.id] })
      void queryClient.invalidateQueries({ queryKey: ['audit', 'working-item', savedItem.id] })

      if (failedPendingIds.length || failedDeleteIds.length) {
        setEditing(savedItem)
        setForm(toForm(savedItem))
        setPendingAttachments((current) => current.filter((item) => {
          if (failedPendingIds.includes(item.id)) return true
          URL.revokeObjectURL(item.previewUrl)
          return false
        }))
        setRemovedAttachmentIds(failedDeleteIds)
        setAttachmentMessage(`工作項目已儲存，但有 ${failedPendingIds.length + failedDeleteIds.length} 張圖片處理失敗，請再儲存一次重試。`)
        return
      }

      close()
      setMessage('Working Item 與圖片附件已儲存。')
    },
    onError: (error) => {
      const latest = conflictCurrent<WorkingItem>(error)
      latest ? setConflict(latest) : setMessage(error instanceof Error ? error.message : '儲存失敗。')
    },
  })

  const close = () => {
    setPendingAttachments((current) => {
      current.forEach((item) => URL.revokeObjectURL(item.previewUrl))
      return []
    })
    setRemovedAttachmentIds([])
    setAttachmentMessage('')
    setAttachmentBusy(false)
    setOpen(false)
    setEditing(null)
    setConflict(null)
    setForm(emptyForm)
    if (requestedItemId) {
      const next = new URLSearchParams(searchParams)
      next.delete('item')
      setSearchParams(next, { replace: true })
    }
  }

  const edit = (row: WorkingItem) => {
    setPendingAttachments([])
    setRemovedAttachmentIds([])
    setAttachmentMessage('')
    setEditing(row)
    setForm(toForm(row))
    setOpen(true)
    setConflict(null)
  }

  useEffect(() => {
    const deepLink = resolveDeepLinkDrawer(requestedItemId, lastOpenedDeepLinkRef.current)
    lastOpenedDeepLinkRef.current = deepLink.lastOpenedId
    if (!deepLink.shouldOpen || open) return
    const row = data.workingItems.find((item) => item.id === requestedItemId)
    if (row) edit(row)
  }, [data.workingItems, open, requestedItemId])

  const create = () => {
    setPendingAttachments([])
    setRemovedAttachmentIds([])
    setAttachmentMessage('')
    setEditing(null)
    setOpen(true)
    setConflict(null)
    if (!form.task) setForm(emptyForm)
  }

  const queueAttachments = async (files: File[]) => {
    const existingCount = (attachmentsQuery.data?.length ?? 0) - removedAttachmentIds.length
    const available = IMAGE_LIMITS.maxAttachments - existingCount - pendingAttachments.length
    if (available <= 0) {
      setAttachmentMessage(`每個 Working Item 最多可保存 ${IMAGE_LIMITS.maxAttachments} 張圖片。`)
      return
    }
    setAttachmentBusy(true)
    setAttachmentMessage('')
    const queued: PendingAttachment[] = []
    const errors: string[] = []
    for (const file of files.slice(0, available)) {
      try {
        const compressed = await compressImageToWebp(file)
        queued.push({
          ...compressed,
          id: crypto.randomUUID(),
          previewUrl: URL.createObjectURL(compressed.file),
        })
      } catch (error) {
        errors.push(error instanceof Error ? error.message : '圖片壓縮失敗。')
      }
    }
    setPendingAttachments((current) => [...current, ...queued])
    if (files.length > available) errors.push(`已達 ${IMAGE_LIMITS.maxAttachments} 張上限，其餘圖片未加入。`)
    setAttachmentMessage(errors[0] ?? (queued.length ? `已將 ${queued.length} 張圖片轉成 WebP，儲存時會上傳。` : ''))
    setAttachmentBusy(false)
  }

  const removePendingAttachment = (id: string) => {
    setPendingAttachments((current) => current.filter((item) => {
      if (item.id !== id) return true
      URL.revokeObjectURL(item.previewUrl)
      return false
    }))
  }

  const discardDraft = () => {
    clearDraft()
    setForm(emptyForm)
    setMessage('本機草稿已清除。')
  }

  const showInlineConflict = (local: WorkingItem, latest: WorkingItem) => {
    setEditing(local)
    setForm(toForm(local))
    setConflict(latest)
  }

  const updateCompletion = async (row: WorkingItem) => {
    if (pendingIds[row.id]) return
    const done = !row.done
    const completionPatch = enforceWorkingItemCompletion({ done }, row)
    patchWorkingItemInCache(row.id, completionPatch)
    setPendingIds((prev) => ({ ...prev, [row.id]: true }))
    try {
      const updated = await api.updateEntity<WorkingItem>('working-items', row.id, row.version, completionPatch)
      updateWorkingItemInCache(updated)
      setMessage(done
        ? `「${row.task}」已標記為完成${row.isPriority ? '並取消重點' : ''}。`
        : `「${row.task}」已取消完成。`)
    } catch (error) {
      patchWorkingItemInCache(row.id, { done: row.done, isPriority: row.isPriority })
      const latest = conflictCurrent<WorkingItem>(error)
      latest ? showInlineConflict(row, latest) : setMessage(error instanceof Error ? error.message : '更新失敗。')
    } finally {
      setPendingIds((prev) => {
        const next = { ...prev }
        delete next[row.id]
        return next
      })
    }
  }

  const togglePriority = async (row: WorkingItem) => {
    if (pendingIds[row.id] || row.done) return
    const isPriority = !row.isPriority
    patchWorkingItemInCache(row.id, { isPriority })
    setPendingIds((prev) => ({ ...prev, [row.id]: true }))
    try {
      const updated = await api.updateEntity<WorkingItem>('working-items', row.id, row.version, { isPriority })
      updateWorkingItemInCache(updated)
      setMessage(isPriority ? `「${row.task}」已設為重點項目。` : `「${row.task}」已取消重點項目。`)
    } catch (error) {
      patchWorkingItemInCache(row.id, { isPriority: row.isPriority })
      const latest = conflictCurrent<WorkingItem>(error)
      latest ? showInlineConflict(row, latest) : setMessage(error instanceof Error ? error.message : '更新失敗。')
    } finally {
      setPendingIds((prev) => {
        const next = { ...prev }
        delete next[row.id]
        return next
      })
    }
  }

  const remove = async (row: WorkingItem) => {
    if (!window.confirm(`確定刪除「${row.task}」？`)) return
    try {
      await api.deleteEntity('working-items', row.id, row.version)
      queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
        if (!old) return old
        return {
          ...old,
          readiness: old.readiness.map((readinessItem) => ({
            ...readinessItem,
            workingItemIds: (readinessItem.workingItemIds ?? []).filter((workingItemId) => workingItemId !== row.id),
          })),
          workingItems: old.workingItems.filter((item) => item.id !== row.id),
        }
      })
      setMessage(`「${row.task}」已刪除。`)
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '刪除失敗。')
    }
  }

  const dropOn = async (event: DragEvent<HTMLTableRowElement>, target: WorkingItem) => {
    event.preventDefault()
    if (tableSort) return
    const dragged = data.workingItems.find((row) => row.id === draggingId)
    setDraggingId(null)
    if (!dragged || dragged.id === target.id) return
    const without = data.workingItems.filter((row) => row.id !== dragged.id)
    const targetIndex = without.findIndex((row) => row.id === target.id)
    const sortOrder = sortOrderBefore(without, targetIndex)

    const reordered = [...data.workingItems]
    const fromIdx = reordered.findIndex((item) => item.id === dragged.id)
    const [moved] = reordered.splice(fromIdx, 1)
    const toIdx = reordered.findIndex((item) => item.id === target.id)
    reordered.splice(toIdx, 0, { ...moved, sortOrder })
    queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
      if (!old) return old
      return { ...old, workingItems: reordered }
    })

    try {
      const updated = await api.updateEntity<WorkingItem>('working-items', dragged.id, dragged.version, { sortOrder })
      updateWorkingItemInCache(updated)
      setMessage('排序已更新。')
    } catch (error) {
      queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
        if (!old) return old
        return { ...old, workingItems: data.workingItems }
      })
      const latest = conflictCurrent<WorkingItem>(error)
      latest ? showInlineConflict(dragged, latest) : setMessage(error instanceof Error ? error.message : '排序失敗。')
    }
  }

  const editingIndex = editing
    ? data.workingItems.findIndex((item) => item.id === editing.id)
    : data.workingItems.length
  const itemNumber = `#${String(Math.max(0, editingIndex) + 1).padStart(2, '0')}`

  const renderRow = (row: WorkingItem, isCompleted = false) => {
    const index = data.workingItems.findIndex((item) => item.id === row.id)
    const rowProjects = workingProjectRefs(row)
    const projectLabel = workingProjectSummary(rowProjects)
    return (
      <tr
        key={row.id}
        className={`clickable-row ${row.done ? 'working-row-done' : ''} ${row.isPriority ? 'working-row-priority' : ''} ${draggingId === row.id ? 'dragging' : ''}`}
        draggable={canEdit && !isCompleted && !tableSort}
        onDragStart={() => !isCompleted && !tableSort && setDraggingId(row.id)}
        onDragEnd={() => !isCompleted && !tableSort && setDraggingId(null)}
        onDragOver={(event) => canEdit && !isCompleted && !tableSort && event.preventDefault()}
        onDrop={(event) => !isCompleted && !tableSort && void dropOn(event, row)}
        onClick={() => canEdit && edit(row)}
      >
        {canEdit ? (
          <td className={`drag-handle ${tableSort ? 'disabled' : ''}`} title={!isCompleted && tableSort ? '排序檢視中；恢復預設後可拖曳' : undefined}>
            {!isCompleted ? <DotsSixVertical size={20} /> : null}
          </td>
        ) : null}
        <td className="item-number">#{String(index + 1).padStart(2, '0')}</td>
        <td>
          <div className="working-project-menu-wrap" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className={`working-project-chip ${rowProjects.some((project) => project.name === selectedProject) || (!rowProjects.length && selectedProject === '不分專案') ? 'active' : ''}`}
              onClick={() => {
                if (rowProjects.length <= 1) {
                  const name = rowProjects[0]?.name ?? '不分專案'
                  setSelectedProject((previous) => previous === name ? null : name)
                } else setOpenProjectsMenuId((current) => current === row.id ? null : row.id)
              }}
              title={rowProjects.length ? rowProjects.map((project) => project.name).join('、') : '不分專案'}
            >
              {projectLabel}{rowProjects.length > 1 ? <CaretDown size={11} /> : null}
            </button>
            {openProjectsMenuId === row.id ? <div className="working-project-popover" role="menu">
              {rowProjects.map((project) => <button key={project.id} type="button" role="menuitem" onClick={() => { setSelectedProject(project.name); setOpenProjectsMenuId(null) }}>{project.name}<small>{project.groupName || project.type}</small></button>)}
            </div> : null}
          </div>
        </td>
        <td className="working-task-cell">
          <div className="task-title-wrap">
            <button
              type="button"
              className={`priority-star-btn ${row.isPriority ? 'active' : ''}`}
              disabled={!canEdit || row.done || Boolean(pendingIds[row.id])}
              aria-label={row.isPriority ? `取消重點項目：${row.task}` : `設為重點項目：${row.task}`}
              title={row.done ? '已完成項目不能設為重點' : row.isPriority ? '取消重點項目' : '設為重點項目'}
              onClick={(event) => { event.stopPropagation(); void togglePriority(row) }}
            >
              <Star size={15} weight={row.isPriority ? 'fill' : 'bold'} />
            </button>
            <strong>{row.task}</strong>
          </div>
          {(row.subtasks?.length || row.attachmentCount || row.links.length) ? (
            <div className="working-task-meta">
            {row.subtasks && row.subtasks.length > 0 ? (
              <button
                type="button"
                className={`subtask-badge-btn ${expandedTasks[row.id] ? 'expanded' : ''}`}
                onClick={(event) => { event.stopPropagation(); toggleExpand(row.id) }}
                title={expandedTasks[row.id] ? '收合細項' : '展開細項'}
              >
                <CaretDown size={13} className={`caret-icon ${expandedTasks[row.id] ? 'open' : ''}`} />
                <span>{row.subtasks.filter((s) => s.done).length}/{row.subtasks.length}</span>
              </button>
            ) : null}
            {row.attachmentCount ? (
              <span className="working-attachment-count" title={`${row.attachmentCount} 張圖片附件`}>
                <Paperclip size={12} weight="bold" />
                {row.attachmentCount}
              </span>
            ) : null}
              {row.links.length > 0 ? (
                <div className="working-links-menu-wrap" onClick={(event) => event.stopPropagation()}>
                  <button
                    type="button"
                    className={`working-links-more ${openLinksMenuId === row.id ? 'active' : ''}`}
                    aria-expanded={openLinksMenuId === row.id}
                    aria-haspopup="menu"
                    aria-label={`顯示 ${row.links.length} 個參考連結`}
                    title={`顯示 ${row.links.length} 個參考連結`}
                    onClick={() => setOpenLinksMenuId((current) => current === row.id ? null : row.id)}
                  >
                    <LinkIcon size={12} weight="bold" />
                    <span>{row.links.length}</span>
                    <CaretDown size={11} className={`caret-icon ${openLinksMenuId === row.id ? 'open' : ''}`} />
                  </button>
                  {openLinksMenuId === row.id ? (
                    <div className="working-links-popover" role="menu">
                      {row.links.map((link) => (
                        <a
                          key={link.id}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="working-link-badge"
                          role="menuitem"
                          title={link.url}
                          onClick={() => setOpenLinksMenuId(null)}
                        >
                          <LinkIcon size={12} weight="bold" />
                          <span>{link.title || link.url}</span>
                        </a>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
          {expandedTasks[row.id] && row.subtasks && row.subtasks.length > 0 ? (
            <div className="subtasks-expand-list" onClick={(event) => event.stopPropagation()}>
              {row.subtasks.map((st) => (
                <label key={st.id} className={`subtask-expand-item ${st.done ? 'done' : ''}`}>
                  <input
                    type="checkbox"
                    checked={st.done}
                    disabled={!canEdit || Boolean(pendingIds[row.id])}
                    onChange={() => void toggleSubtask(row, st.id)}
                  />
                  <span>{st.title}</span>
                </label>
              ))}
            </div>
          ) : null}
        </td>
        <td className="working-owner-column">
          <OwnerTags
            value={row.owner}
            selectedOwner={selectedOwner}
            onToggleOwner={(owner) => setSelectedOwner((prev) => (prev === owner ? null : owner))}
          />
        </td>
        <td className="working-date">{row.startDate || '—'}</td>
        <td className="working-date">{row.dueDate || '—'}</td>
        <td className="center-column">
          <span className={`working-status-light ${workingStatusLight(row.status)}`} role="img" aria-label={`狀態：${workingStatusLabel(row.status)}`} title={workingStatusLabel(row.status)} />
        </td>
        <td className="center-column">
          <button
            className={`working-check ${row.done ? 'checked' : ''}`}
            type="button"
            disabled={!canEdit || Boolean(pendingIds[row.id])}
            aria-label={row.done ? `取消完成：${row.task}` : `標記完成：${row.task}`}
            aria-pressed={row.done}
            onClick={(event) => { event.stopPropagation(); void updateCompletion(row) }}
          >
            {row.done ? <Check size={15} weight="bold" /> : null}
          </button>
        </td>
        <td className="wide-cell notes-cell"><div className="notes-preview"><FormattedText text={row.notes} /></div></td>
        {isAdmin ? <td><button className="icon-button danger" aria-label="刪除" onClick={(event) => { event.stopPropagation(); void remove(row) }}><Trash size={17} /></button></td> : null}
      </tr>
    )
  }

  return (
    <section className="page-stack">
      <div className="working-header-bar">
        <div className="working-header-title-group">
          <h1>Working Item</h1>
          <div className="working-stat-pills">
            <span className="stat-pill total">全部 <strong>{data.workingItems.length}</strong></span>
            <span className="stat-pill green">正常 <strong>{data.workingItems.filter((row) => workingStatusLight(row.status) === 'green').length}</strong></span>
            <span
              className={`stat-pill blue clickable ${completedOpen ? 'active' : ''}`}
              onClick={() => setCompletedOpen((prev) => !prev)}
              title={completedOpen ? '點擊收合已完成項目' : '點擊展開已完成項目'}
            >
              已完成 <strong>{data.workingItems.filter((row) => row.done).length}</strong>
            </span>
            {data.workingItems.filter((row) => row.isPriority).length > 0 ? (
              <span
                className={`stat-pill amber clickable ${priorityOnly ? 'active' : ''}`}
                onClick={() => setPriorityOnly((prev) => !prev)}
                title={priorityOnly ? '點擊顯示全部項目' : '點擊僅篩選重點項目'}
              >
                <Star size={13} weight="fill" /> 重點 <strong>{data.workingItems.filter((row) => row.isPriority).length}</strong>
              </span>
            ) : null}
            {data.workingItems.filter((row) => workingStatusLight(row.status) === 'red').length > 0 ? (
              <span className="stat-pill red">阻塞 <strong>{data.workingItems.filter((row) => workingStatusLight(row.status) === 'red').length}</strong></span>
            ) : null}
          </div>
        </div>
        {canEdit ? (
          <Button variant="primary" onClick={create}><Plus size={16} weight="bold" />新增 Working Item</Button>
        ) : null}
      </div>
      {canEdit && !open && form.task ? (
        <div className="draft-banner">
          <div><Warning size={20} weight="fill" /><span><strong>找到尚未送出的本機草稿</strong> · {form.task}</span></div>
          <div><Button onClick={discardDraft}>清除</Button><Button variant="primary" onClick={create}>繼續編輯</Button></div>
        </div>
      ) : null}
      {message ? (
        <div className="floating-toast">
          <span>{message}</span>
          <button type="button" onClick={() => setMessage('')} aria-label="關閉通知">×</button>
        </div>
      ) : null}
      <Panel>
        <div className="panel-toolbar">
          <div><h2>Team Action List</h2></div>
          <div className="filters">
            <input className="search-input" placeholder="搜尋工作、Project 或 Owner" value={query} onChange={(event) => setQuery(event.target.value)} />
            <button
              type="button"
              className={`priority-filter-btn ${priorityOnly ? 'active' : ''}`}
              onClick={() => setPriorityOnly((prev) => !prev)}
              title={priorityOnly ? '顯示全部項目' : '僅顯示重點項目'}
            >
              <Star size={15} weight={priorityOnly ? 'fill' : 'bold'} />
              <span>重點項目{data.workingItems.filter((item) => item.isPriority).length > 0 ? ` (${data.workingItems.filter((item) => item.isPriority).length})` : ''}</span>
            </button>
            {selectedProject ? (
              <div className="owner-filter-chip project-filter-chip">
                <span>專案: <strong>{selectedProject}</strong></span>
                <button type="button" onClick={() => setSelectedProject(null)} aria-label="清除專案篩選">×</button>
              </div>
            ) : null}
            {selectedOwner ? (
              <div className="owner-filter-chip">
                <span>負責人: <strong>{selectedOwner}</strong></span>
                <button type="button" onClick={() => setSelectedOwner(null)} aria-label="清除負責人篩選">×</button>
              </div>
            ) : null}
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option>全部</option>
              {workingStatusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>
        </div>
        {tableSort ? (
          <div className="working-sort-summary" role="status">
            <span>排序：<strong>{workingSortLabels[tableSort.key]}</strong> {tableSort.direction === 'asc' ? '↑' : '↓'} · 排序期間暫停拖曳</span>
            <button type="button" onClick={() => setTableSort(null)}>恢復預設</button>
          </div>
        ) : null}
        <div className="table-wrap working-table">
          <table>
            <colgroup>
              {canEdit ? <col className="working-col-drag" /> : null}
              <col className="working-col-number" />
              <col className="working-col-project" />
              <col className="working-col-task" />
              <col className="working-col-owner" />
              <col className="working-col-date" />
              <col className="working-col-date" />
              <col className="working-col-status" />
              <col className="working-col-done" />
              <col className="working-col-notes" />
              {isAdmin ? <col className="working-col-actions" /> : null}
            </colgroup>
            <thead>
              <tr>
                {canEdit ? <th className="drag-column" /> : null}
                <th>#</th>
                <WorkingSortHeader label="Project" sortKey="project" sort={tableSort} onSort={() => changeTableSort('project')} />
                <th>Task</th><th className="working-owner-column">Owner</th>
                <WorkingSortHeader label="Start" sortKey="startDate" sort={tableSort} onSort={() => changeTableSort('startDate')} />
                <WorkingSortHeader label="Due" sortKey="dueDate" sort={tableSort} onSort={() => changeTableSort('dueDate')} />
                <WorkingSortHeader label="Status" sortKey="status" sort={tableSort} onSort={() => changeTableSort('status')} className="center-column" />
                <th className="center-column">完成</th><th>Notes</th>
                {isAdmin ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {activeRows.map((row) => renderRow(row, false))}
              {activeRows.length === 0 ? (
                <tr>
                  <td className="table-empty" colSpan={9 + (canEdit ? 1 : 0) + (isAdmin ? 1 : 0)}>
                    {completedRows.length > 0 ? '所有進行中工作皆已完成！🎉' : '找不到符合條件的 Working Item。'}
                  </td>
                </tr>
              ) : null}
            </tbody>
            {completedRows.length > 0 ? (
              <tbody className="completed-tbody">
                <tr className="completed-toggle-tr">
                  <td colSpan={9 + (canEdit ? 1 : 0) + (isAdmin ? 1 : 0)} className="completed-toggle-td">
                    <button
                      type="button"
                      className="completed-section-toggle"
                      onClick={() => setCompletedOpen((prev) => !prev)}
                      aria-expanded={completedOpen}
                    >
                      <div className="completed-toggle-left">
                        <CaretDown size={14} weight="bold" className={`completed-caret ${completedOpen ? 'open' : ''}`} />
                        <span>已完成項目 ({completedRows.length})</span>
                      </div>
                      <span className="completed-toggle-hint">
                        {completedOpen ? '點擊收合' : '點擊展開'}
                      </span>
                    </button>
                  </td>
                </tr>
                {completedOpen ? completedRows.map((row) => renderRow(row, true)) : null}
              </tbody>
            ) : null}
          </table>
        </div>
      </Panel>
      <Drawer
        open={open}
        title={editing ? `編輯工作任務 · ${itemNumber}` : '新增工作任務'}
        subtitle={editing ? `版本 ${editing.version} · 完整異動請查看下方修改紀錄` : '輸入內容會自動暫存在這台瀏覽器'}
        onClose={close}
        footer={<><Button onClick={close}>取消</Button><Button variant="primary" type="submit" form="working-form" disabled={save.isPending || attachmentBusy}>{save.isPending ? '儲存中…' : attachmentBusy ? '圖片處理中…' : '儲存'}</Button></>}
      >
        <WorkingFormView
          form={form}
          setForm={setForm}
          projects={data.projects}
          readinessItems={data.readiness}
          itemNumber={itemNumber}
          workingItemId={editing?.id}
          existingAttachments={(attachmentsQuery.data ?? []).filter((attachment) => !removedAttachmentIds.includes(attachment.id))}
          pendingAttachments={pendingAttachments}
          removedAttachmentCount={removedAttachmentIds.length}
          attachmentsLoading={attachmentsQuery.isLoading}
          attachmentBusy={attachmentBusy}
          attachmentMessage={attachmentMessage}
          onQueueAttachments={(files) => { void queueAttachments(files) }}
          onRemoveExisting={(id) => setRemovedAttachmentIds((current) => [...current, id])}
          onRemovePending={removePendingAttachment}
          onSubmit={(event) => { event.preventDefault(); save.mutate() }}
        />
        <HistoryPanel entity="working-item" id={editing?.id} />
      </Drawer>
      <ConflictDialog
        open={Boolean(conflict)}
        local={form as unknown as Record<string, unknown>}
        remote={(conflict ?? {}) as unknown as Record<string, unknown>}
        labels={labels}
        onClose={() => setConflict(null)}
        onUseLatest={() => {
          if (conflict) {
            setEditing(conflict)
            setForm(toForm(conflict))
            setOpen(true)
            setConflict(null)
          }
        }}
      />
    </section>
  )
}

export function WorkingFormView({
  form,
  setForm,
  projects,
  readinessItems,
  itemNumber,
  workingItemId,
  existingAttachments,
  pendingAttachments,
  removedAttachmentCount,
  attachmentsLoading,
  attachmentBusy,
  attachmentMessage,
  onQueueAttachments,
  onRemoveExisting,
  onRemovePending,
  onSubmit,
  formId = 'working-form',
}: {
  form: WorkingForm
  setForm: (value: WorkingForm) => void
  projects: Project[]
  readinessItems: ReadinessItem[]
  itemNumber: string
  workingItemId?: string
  existingAttachments: WorkingAttachment[]
  pendingAttachments: PendingAttachment[]
  removedAttachmentCount: number
  attachmentsLoading: boolean
  attachmentBusy: boolean
  attachmentMessage: string
  onQueueAttachments: (files: File[]) => void
  onRemoveExisting: (id: string) => void
  onRemovePending: (id: string) => void
  onSubmit: (event: FormEvent) => void
  formId?: string
}) {
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('')
  const [newLinkTitle, setNewLinkTitle] = useState('')
  const [newLinkUrl, setNewLinkUrl] = useState('')
  const [readinessPickerOpen, setReadinessPickerOpen] = useState(false)
  const [readinessQuery, setReadinessQuery] = useState('')
  const [projectPickerOpen, setProjectPickerOpen] = useState(false)
  const [projectSelectionMessage, setProjectSelectionMessage] = useState('')
  const projectPickerRef = useRef<HTMLDivElement>(null)
  const notesRef = useRef<HTMLTextAreaElement>(null)
  const legacyProjectName = (form as WorkingForm & { projectName?: string }).projectName
  const selectedProjectIds = Array.isArray(form.projectIds)
    ? form.projectIds
    : projects.filter((project) => project.name === legacyProjectName).map((project) => project.id)

  useEffect(() => {
    if (!projectPickerOpen) return
    const closeOnOutside = (event: PointerEvent) => {
      if (!projectPickerRef.current?.contains(event.target as Node)) setProjectPickerOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setProjectPickerOpen(false)
    }
    document.addEventListener('pointerdown', closeOnOutside)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutside)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [projectPickerOpen])

  const selectedOwners = parseOwners(form.owner)
  const toggleOwner = (owner: string) => {
    const nextOwners = selectedOwners.includes(owner)
      ? selectedOwners.filter((value) => value !== owner)
      : [...selectedOwners, owner]
    setForm({ ...form, owner: nextOwners.join(', ') })
  }

  const addSubtask = () => {
    if (!newSubtaskTitle.trim()) return
    const item: Subtask = {
      id: `st-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      title: newSubtaskTitle.trim(),
      done: false,
    }
    setForm({ ...form, subtasks: [...(form.subtasks || []), item] })
    setNewSubtaskTitle('')
  }

  const removeSubtask = (id: string) => {
    setForm({ ...form, subtasks: (form.subtasks || []).filter((s) => s.id !== id) })
  }

  const moveSubtask = (index: number, direction: 'up' | 'down') => {
    const list = [...(form.subtasks || [])]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= list.length) return
    const temp = list[index]
    list[index] = list[targetIndex]
    list[targetIndex] = temp
    setForm({ ...form, subtasks: list })
  }

  const addLink = () => {
    if (!newLinkUrl.trim()) return
    let url = newLinkUrl.trim()
    if (!/^https?:\/\//i.test(url)) {
      url = `https://${url}`
    }
    const item: WorkingLink = {
      id: `lk-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      title: newLinkTitle.trim() || url,
      url,
    }
    setForm({ ...form, links: [...(form.links || []), item] })
    setNewLinkTitle('')
    setNewLinkUrl('')
  }

  const removeLink = (id: string) => {
    setForm({ ...form, links: (form.links || []).filter((l) => l.id !== id) })
  }

  const insertTodayDate = () => {
    const notes = notesRef.current
    const cursor = notes?.selectionStart ?? form.notes.length
    const stamp = taipeiDateStamp()
    const before = form.notes.slice(0, cursor)
    const after = form.notes.slice(cursor)
    const lineBreakBefore = before && !before.endsWith('\n') ? '\n' : ''
    const lineBreakAfter = after && !after.startsWith('\n') ? '\n' : ''
    setForm({ ...form, notes: `${before}${lineBreakBefore}${stamp}${lineBreakAfter}${after}` })
    const nextCursor = before.length + lineBreakBefore.length + stamp.length
    requestAnimationFrame(() => {
      notes?.focus()
      notes?.setSelectionRange(nextCursor, nextCursor)
    })
  }

  const selectedReadinessItems = readinessItems.filter((item) => form.readinessItemIds.includes(item.id))
  const selectedProjectNames = new Set(projects.filter((project) => selectedProjectIds.includes(project.id)).map((project) => project.name))
  const selectedProjects = projects.filter((project) => selectedProjectIds.includes(project.id))
  const selectedProjectSummary = workingProjectSummary(selectedProjects)
  const projectGroups = [...projects.reduce((groups, project) => {
    const group = project.groupName.trim() || '其他專案'
    groups.set(group, [...(groups.get(group) ?? []), project])
    return groups
  }, new Map<string, Project[]>()).entries()]
  const availableReadinessItems = readinessItems.filter((item) => (
    selectedProjectNames.has(item.projectName)
    && `${item.projectName} ${item.groupName} ${item.item}`.toLowerCase().includes(readinessQuery.trim().toLowerCase())
  ))
  const availableReadinessGroups = [...availableReadinessItems.reduce((groups, item) => {
    groups.set(item.projectName, [...(groups.get(item.projectName) ?? []), item])
    return groups
  }, new Map<string, ReadinessItem[]>()).entries()]
  const setProjectIds = (nextIds: string[]) => {
    const nextNames = new Set(projects.filter((project) => nextIds.includes(project.id)).map((project) => project.name))
    const blocked = selectedReadinessItems.find((item) => !nextNames.has(item.projectName))
    if (blocked) {
      setProjectSelectionMessage(`請先解除「${blocked.projectName} / ${blocked.item}」的 Test Item 關聯。`)
      return
    }
    setProjectSelectionMessage('')
    setForm({ ...form, projectIds: nextIds })
    if (!nextIds.length) setReadinessPickerOpen(false)
  }
  const toggleProject = (projectId: string) => setProjectIds(
    selectedProjectIds.includes(projectId)
      ? selectedProjectIds.filter((id) => id !== projectId)
      : [...selectedProjectIds, projectId],
  )
  const toggleReadinessItem = (id: string) => {
    setForm({
      ...form,
      readinessItemIds: form.readinessItemIds.includes(id)
        ? form.readinessItemIds.filter((value) => value !== id)
        : [...form.readinessItemIds, id],
    })
  }

  return (
    <form
      id={formId}
      className="form-grid two"
      onSubmit={onSubmit}
      onPaste={(event) => {
        const files = [...event.clipboardData.items]
          .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
          .map((item) => item.getAsFile())
          .filter((file): file is File => Boolean(file))
        if (files.length) {
          event.preventDefault()
          onQueueAttachments(files)
        }
      }}
    >
      <Field label="項目編號（依排序自動編號）"><input value={itemNumber} readOnly /></Field>
      <div ref={projectPickerRef} className="field working-project-selector-field">
        <label>專案平台（可複選）</label>
        <button type="button" className={`working-project-picker-toggle ${projectPickerOpen ? 'open' : ''}`} aria-expanded={projectPickerOpen} onClick={() => setProjectPickerOpen((value) => !value)}>
          <span>{selectedProjectSummary}</span>
          {selectedProjectIds.length ? <small>{selectedProjectIds.length} 個 Project</small> : null}
          <CaretDown size={14} className={`caret-icon ${projectPickerOpen ? 'open' : ''}`} />
        </button>
        {projectPickerOpen ? <div className="working-project-selector">
          {projectGroups.map(([groupName, groupProjects]) => {
            const allSelected = groupProjects.every((project) => selectedProjectIds.includes(project.id))
            return <div key={groupName} className="working-project-group">
              <div><strong>{groupName}</strong>{groupName !== '其他專案' ? <button type="button" onClick={() => setProjectIds(allSelected ? selectedProjectIds.filter((id) => !groupProjects.some((project) => project.id === id)) : [...new Set([...selectedProjectIds, ...groupProjects.map((project) => project.id)])])}>{allSelected ? '取消全選' : `全選 ${groupName}`}</button> : null}</div>
              <div>{groupProjects.map((project) => <button key={project.id} type="button" className={selectedProjectIds.includes(project.id) ? 'selected' : ''} onClick={() => toggleProject(project.id)}>{selectedProjectIds.includes(project.id) ? '✓' : '+'} {project.name}</button>)}</div>
            </div>
          })}
          <button type="button" className={`working-project-unassigned ${!selectedProjectIds.length ? 'selected' : ''}`} onClick={() => setProjectIds([])}>不分專案</button>
        </div> : null}
        {projectSelectionMessage ? <small className="form-error">{projectSelectionMessage}</small> : null}
      </div>
      <div className="drawer-readiness-links-section">
        <div className="drawer-section-title">
          <span>關聯 Test Item ({selectedReadinessItems.length})</span>
          <span className="drawer-section-note">{selectedProjectIds.length ? '僅顯示已選 Project' : '不分專案不提供關聯'}</span>
        </div>
        {selectedReadinessItems.length ? (
          <div className="readiness-link-selection">
            {selectedReadinessItems.map((item) => (
              <div key={item.id} className="readiness-link-selected-row">
                <LinkIcon size={14} weight="bold" />
                <strong title={`${item.projectName}｜${item.groupName}｜${item.item}`}>{item.projectName}｜{item.groupName}｜{item.item}</strong>
                <button type="button" onClick={() => toggleReadinessItem(item.id)} aria-label={`解除關聯：${item.projectName} / ${item.groupName} / ${item.item}`} title="解除關聯"><X size={14} /></button>
              </div>
            ))}
          </div>
        ) : <p className="readiness-link-empty">尚未關聯 Test Item</p>}
        <button
          type="button"
          className="readiness-link-picker-toggle"
          disabled={!selectedProjectIds.length}
          aria-expanded={readinessPickerOpen}
          onClick={() => setReadinessPickerOpen((value) => !value)}
        >
          <Plus size={14} weight="bold" />選擇 Test Item
        </button>
        {readinessPickerOpen ? (
          <div className="readiness-link-picker">
            <input value={readinessQuery} onChange={(event) => setReadinessQuery(event.target.value)} placeholder="搜尋 Project、類別或 Test Item" autoFocus />
            <div className="readiness-link-options">
              {availableReadinessGroups.map(([projectName, items]) => (
                <section key={projectName} className="readiness-link-project-group">
                  <h4>{projectName}</h4>
                  <div>
                    {items.map((item) => {
                      const label = `${item.projectName}｜${item.groupName}｜${item.item}`
                      return <label key={item.id} className={form.readinessItemIds.includes(item.id) ? 'selected' : ''} title={label}>
                        <input type="checkbox" checked={form.readinessItemIds.includes(item.id)} onChange={() => toggleReadinessItem(item.id)} />
                        <span>{label}</span>
                      </label>
                    })}
                  </div>
                </section>
              ))}
              {!availableReadinessItems.length ? <p>此 Project 沒有符合的 Test Item。</p> : null}
            </div>
          </div>
        ) : null}
      </div>
      <Field label="工作任務"><textarea rows={4} required value={form.task} onChange={(event) => setForm({ ...form, task: event.target.value })} /></Field>
      <Field label="負責人">
        <div className="owner-selector" role="group" aria-label="選擇負責人">
          {workingItemOwners.map((owner) => {
            const selected = selectedOwners.includes(owner)
            return <button key={owner} type="button" className={selected ? 'selected' : ''} aria-pressed={selected} onClick={() => toggleOwner(owner)}>{selected ? '✓' : '+'} {owner}</button>
          })}
        </div>
        <input aria-label="負責人清單" value={form.owner} onChange={(event) => setForm({ ...form, owner: event.target.value })} placeholder="也可以直接輸入其他負責人，以逗號分隔" />
      </Field>
      <Field label="開始時間"><input value={form.startDate} onChange={(event) => setForm({ ...form, startDate: event.target.value })} /></Field>
      <Field label="完成時間"><input value={form.dueDate} onChange={(event) => setForm({ ...form, dueDate: event.target.value })} /></Field>
      <Field label="狀態燈號">
        <select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value as WorkingForm['status'] })}>
          {workingStatusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </Field>
      <Field label="重點與完成標記">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <label className="check-control">
            <input type="checkbox" checked={form.isPriority} disabled={form.done} onChange={(event) => setForm({ ...form, isPriority: event.target.checked })} />
            <Star size={18} weight={form.isPriority ? 'fill' : 'bold'} style={{ color: form.isPriority ? '#f59e0b' : '#9aa8b6' }} />
            重點項目
          </label>
          <label className="check-control">
            <input type="checkbox" checked={form.done} onChange={(event) => setForm({ ...form, ...enforceWorkingItemCompletion({ done: event.target.checked }, form) })} />
            <CheckCircle size={18} />
            標記完成
          </label>
        </div>
      </Field>
      <div className="drawer-subtasks-section">
        <div className="drawer-section-title">
          <span>工作細項清單 ({form.subtasks?.length || 0})</span>
        </div>
        {form.subtasks && form.subtasks.length > 0 ? (
          <div>
            {form.subtasks.map((st, idx) => (
              <div key={st.id} className="subtask-drawer-row">
                <input
                  type="checkbox"
                  checked={st.done}
                  onChange={(e) => {
                    const list = (form.subtasks || []).map((s) => s.id === st.id ? { ...s, done: e.target.checked } : s)
                    setForm({ ...form, subtasks: list })
                  }}
                />
                <input
                  type="text"
                  className="subtask-title-input"
                  value={st.title}
                  onChange={(e) => {
                    const list = (form.subtasks || []).map((s) => s.id === st.id ? { ...s, title: e.target.value } : s)
                    setForm({ ...form, subtasks: list })
                  }}
                  placeholder="細項名稱"
                />
                <div className="subtask-btn-group">
                  <button
                    type="button"
                    disabled={idx === 0}
                    onClick={() => moveSubtask(idx, 'up')}
                    title="上移"
                  >
                    <ArrowUp size={13} />
                  </button>
                  <button
                    type="button"
                    disabled={idx === (form.subtasks || []).length - 1}
                    onClick={() => moveSubtask(idx, 'down')}
                    title="下移"
                  >
                    <ArrowDown size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeSubtask(st.id)}
                    title="刪除"
                  >
                    <Trash size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
        <div className="add-subtask-bar">
          <input
            value={newSubtaskTitle}
            onChange={(e) => setNewSubtaskTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addSubtask()
              }
            }}
            placeholder="新增細項（輸入後按 Enter 或點擊新增）"
          />
          <Button type="button" onClick={addSubtask}><Plus size={14} />新增細項</Button>
        </div>
      </div>
      <div className="drawer-links-section">
        <div className="drawer-section-title">
          <span>參考連結 / 網址 ({form.links?.length || 0})</span>
        </div>
        {form.links && form.links.length > 0 ? (
          <div>
            {form.links.map((link) => (
              <div key={link.id} className="link-drawer-row">
                <button
                  type="button"
                  className="link-open-button"
                  disabled={!isSafeHttpUrl(link.url)}
                  aria-label={`開啟連結：${link.title || link.url}`}
                  title={isSafeHttpUrl(link.url) ? '以新分頁開啟連結' : '請先輸入有效的 http/https 網址'}
                  onClick={() => window.open(link.url, '_blank', 'noopener,noreferrer')}
                >
                  <LinkIcon size={14} weight="bold" />
                </button>
                <input
                  type="text"
                  className="link-title-input"
                  value={link.title}
                  onChange={(e) => {
                    const list = (form.links || []).map((l) => l.id === link.id ? { ...l, title: e.target.value } : l)
                    setForm({ ...form, links: list })
                  }}
                  placeholder="連結名稱"
                />
                <input
                  type="text"
                  className="link-url-input"
                  value={link.url}
                  onChange={(e) => {
                    const list = (form.links || []).map((l) => l.id === link.id ? { ...l, url: e.target.value } : l)
                    setForm({ ...form, links: list })
                  }}
                  placeholder="https://..."
                />
                <button
                  type="button"
                  className="icon-button danger"
                  onClick={() => removeLink(link.id)}
                  title="刪除連結"
                >
                  <Trash size={13} />
                </button>
              </div>
            ))}
          </div>
        ) : null}
        <div className="add-link-bar">
          <input
            style={{ width: '130px', flexShrink: 0 }}
            value={newLinkTitle}
            onChange={(e) => setNewLinkTitle(e.target.value)}
            placeholder="名稱（選填）"
          />
          <input
            style={{ flex: 1 }}
            value={newLinkUrl}
            onChange={(e) => setNewLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addLink()
              }
            }}
            placeholder="https://..."
          />
          <Button type="button" onClick={addLink}><Plus size={14} />新增連結</Button>
        </div>
      </div>
      <WorkingAttachmentsEditor
        workingItemId={workingItemId}
        existing={existingAttachments}
        pending={pendingAttachments}
        removedCount={removedAttachmentCount}
        loading={attachmentsLoading}
        busy={attachmentBusy}
        message={attachmentMessage}
        onQueue={onQueueAttachments}
        onRemoveExisting={onRemoveExisting}
        onRemovePending={onRemovePending}
      />
      <div className="field working-notes-field">
        <div className="working-notes-label-row">
          <label className="field-label" htmlFor={`${formId}-notes`}>備註</label>
          <button type="button" className="working-notes-date-button" aria-label="插入今天日期" title="插入今天日期（Alt + Shift + D）" onClick={insertTodayDate}>
            <CalendarPlus size={14} weight="bold" />插入今天日期
          </button>
        </div>
        <textarea
          ref={notesRef}
          id={`${formId}-notes`}
          rows={6}
          value={form.notes}
          onChange={(event) => setForm({ ...form, notes: event.target.value })}
          onKeyDown={(event) => {
            if (event.altKey && event.shiftKey && event.key.toLowerCase() === 'd') {
              event.preventDefault()
              insertTodayDate()
            }
          }}
          placeholder="支援直接貼上網址，檢視時會自動轉為可點擊連結"
        />
      </div>
    </form>
  )
}

function WorkingAttachmentsEditor({
  workingItemId,
  existing,
  pending,
  removedCount,
  loading,
  busy,
  message,
  onQueue,
  onRemoveExisting,
  onRemovePending,
}: {
  workingItemId?: string
  existing: WorkingAttachment[]
  pending: PendingAttachment[]
  removedCount: number
  loading: boolean
  busy: boolean
  message: string
  onQueue: (files: File[]) => void
  onRemoveExisting: (id: string) => void
  onRemovePending: (id: string) => void
}) {
  const [dragActive, setDragActive] = useState(false)
  const [preview, setPreview] = useState<{ src: string; name: string } | null>(null)
  const total = existing.length + pending.length

  useEffect(() => {
    if (!preview) return
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPreview(null)
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [preview])

  const acceptFiles = (files: FileList | File[]) => {
    const images = [...files].filter((file) => file.type.startsWith('image/'))
    if (images.length) onQueue(images)
  }

  return (
    <div className="drawer-attachments-section">
      <div className="drawer-section-title">
        <span>圖片附件 ({total})</span>
        <span className="attachment-limit">最多 {IMAGE_LIMITS.maxAttachments} 張</span>
      </div>
      {loading ? <p className="attachment-status">載入圖片附件中…</p> : null}
      {total > 0 ? (
        <div className="attachment-grid">
          {existing.map((attachment) => {
            const src = workingItemId ? api.workingAttachmentUrl(workingItemId, attachment.id) : ''
            return (
              <article key={attachment.id} className="attachment-card">
                <button type="button" className="attachment-preview-button" onClick={() => setPreview({ src, name: attachment.originalName })}>
                  <img src={src} alt={attachment.caption || attachment.originalName} loading="lazy" />
                </button>
                <div className="attachment-card-meta">
                  <strong title={attachment.originalName}>{attachment.originalName}</strong>
                  <small>{formatBytes(attachment.storedSize)} · WebP</small>
                </div>
                <button type="button" className="attachment-remove" onClick={() => onRemoveExisting(attachment.id)} title="儲存後刪除圖片" aria-label={`刪除圖片：${attachment.originalName}`}>
                  <Trash size={14} />
                </button>
              </article>
            )
          })}
          {pending.map((attachment) => {
            const saved = Math.max(0, attachment.originalSize - attachment.file.size)
            const savedPercent = attachment.originalSize ? Math.round(saved / attachment.originalSize * 100) : 0
            return (
              <article key={attachment.id} className="attachment-card pending">
                <button type="button" className="attachment-preview-button" onClick={() => setPreview({ src: attachment.previewUrl, name: attachment.originalName })}>
                  <img src={attachment.previewUrl} alt={attachment.originalName} />
                  <span>待上傳</span>
                </button>
                <div className="attachment-card-meta">
                  <strong title={attachment.originalName}>{attachment.originalName}</strong>
                  <small>{formatBytes(attachment.originalSize)} → {formatBytes(attachment.file.size)}{savedPercent > 0 ? `（-${savedPercent}%）` : ''}</small>
                </div>
                <button type="button" className="attachment-remove" onClick={() => onRemovePending(attachment.id)} title="移除圖片" aria-label={`移除圖片：${attachment.originalName}`}>
                  <X size={14} />
                </button>
              </article>
            )
          })}
        </div>
      ) : null}
      <label
        className={`attachment-dropzone ${dragActive ? 'drag-active' : ''} ${busy ? 'busy' : ''}`}
        onDragEnter={(event) => { event.preventDefault(); setDragActive(true) }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragActive(false)
        }}
        onDrop={(event) => {
          event.preventDefault()
          setDragActive(false)
          acceptFiles(event.dataTransfer.files)
        }}
      >
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          multiple
          disabled={busy || total >= IMAGE_LIMITS.maxAttachments}
          onChange={(event) => {
            if (event.target.files) acceptFiles(event.target.files)
            event.target.value = ''
          }}
        />
        {busy ? <span>正在轉換成 WebP…</span> : (
          <>
            <UploadSimple size={20} />
            <span>貼上、拖曳圖片，或點擊選擇檔案</span>
            <small>自動縮至 1920px 並轉成 WebP</small>
          </>
        )}
      </label>
      {removedCount > 0 ? <p className="attachment-status warning">儲存後將刪除 {removedCount} 張圖片。</p> : null}
      {message ? <p className="attachment-status">{message}</p> : null}
      {preview ? createPortal(
        <div className="attachment-lightbox" role="dialog" aria-modal="true" aria-label={`圖片預覽：${preview.name}`} onClick={() => setPreview(null)}>
          <button type="button" className="attachment-lightbox-close" onClick={() => setPreview(null)} aria-label="關閉圖片預覽"><X size={22} /></button>
          <img src={preview.src} alt={preview.name} onClick={(event) => event.stopPropagation()} />
          <span>{preview.name}</span>
        </div>,
        document.body,
      ) : null}
    </div>
  )
}

function FormattedText({ text }: { text: string }) {
  if (!text) return <span className="muted-dash">—</span>
  const urlRegex = /(https?:\/\/[^\s]+)|(\[[^\]]+\]\((https?:\/\/[^\s)]+)\))/g
  const parts: ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    if (match[1]) {
      const url = match[1]
      parts.push(
        <a
          key={match.index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {url}
        </a>,
      )
    } else if (match[2] && match[3]) {
      const full = match[2]
      const title = full.slice(1, full.indexOf(']'))
      const url = match[3]
      parts.push(
        <a
          key={match.index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {title}
        </a>,
      )
    }
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return <span className="formatted-text">{parts}</span>
}

function OwnerTags({
  value,
  selectedOwner,
  onToggleOwner,
}: {
  value: string
  selectedOwner?: string | null
  onToggleOwner?: (owner: string) => void
}) {
  const owners = parseOwners(value)
  if (owners.length === 0) return <span className="muted-dash">—</span>
  return (
    <div className="working-owner-tags">
      {owners.map((owner) => {
        const isSelected = selectedOwner === owner
        return (
          <button
            key={owner}
            type="button"
            className={`working-owner-chip ${isSelected ? 'active' : ''}`}
            onClick={(event) => {
              event.stopPropagation()
              onToggleOwner?.(owner)
            }}
            title={isSelected ? `點擊取消篩選：${owner}` : `點擊篩選負責人：${owner}`}
          >
            {owner}
          </button>
        )
      })}
    </div>
  )
}

function parseOwners(value: string) {
  return value.split(',').map((owner) => owner.trim()).filter(Boolean)
}

function isSafeHttpUrl(value: string) {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

export function toWorkingForm(row: WorkingItem): WorkingForm {
  const { task, owner, startDate, dueDate, status, done, isPriority, subtasks, links, notes, readinessItemIds } = row
  return {
    projectIds: Array.isArray(row.projectIds) ? row.projectIds : [],
    task,
    owner,
    startDate,
    dueDate,
    status: workingStatusLight(status),
    done,
    isPriority: Boolean(isPriority),
    subtasks: Array.isArray(subtasks) ? subtasks : [],
    links: Array.isArray(links) ? links : [],
    notes,
    readinessItemIds: Array.isArray(readinessItemIds) ? readinessItemIds : [],
  }
}

const toForm = toWorkingForm
